import { useLiveRefresh } from "../utils/live";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import Modal from "../components/common/Modal";
import QuoteBuilder from "../components/admin/QuoteBuilder";
import LeadConversation from "../components/leads/LeadConversation";
import { listLeads, createLead, updateLead, deleteLead, customerForLead } from "../api/leads";
import { formatMoney as formatUSD } from "../utils/money";
import { telHref, smsHref, mailtoHref, firstName, senderName, timeAgo } from "../utils/contact";
import { LOST_REASONS } from "../utils/followups";
import { notifyCountsChanged } from "../utils/navCounts";
import { campaignLine } from "../utils/campaign";

// The front door: every enquiry — website form or a call you logged — lands
// here, and "Quote this" turns it into a quote with the customer filled in.

const FILTERS = [
  { key: "new", label: "New" },
  { key: "quoted", label: "Quoted" },
  { key: "won", label: "Booked" },
  { key: "lost", label: "Lost" },
  { key: "all", label: "All" },
];

const STAGE_BADGE = {
  new: { label: "New", cls: "bg-birdie-mustard/25 text-birdie-mustard-dark" },
  quoted: { label: "Quoted", cls: "bg-blue-100 text-blue-700" },
  won: { label: "Booked", cls: "bg-birdie-green/15 text-birdie-green" },
  lost: { label: "Lost", cls: "bg-birdie-warmgray/20 text-birdie-charcoal/60" },
};

const SOURCE_LABEL = { website: "Website", phone: "Phone", text: "Text", other: "Other" };

function referenceFor(lead) {
  const lines = [];
  lines.push(
    `${SOURCE_LABEL[lead.source] || "Lead"} · ${new Date(lead.created_at).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`
  );
  if (lead.event_date_text) lines.push(`Event date: ${lead.event_date_text}`);
  if (lead.event_type) lines.push(`Event type: ${lead.event_type}`);
  if (lead.message) lines.push(`"${lead.message}"`);
  if (lead.notes) lines.push(`Your notes: ${lead.notes}`);
  return lines.join("\n");
}

export default function AdminInbox() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("new");
  // ?lead=<id> (from search): show everything and bring that lead into view.
  const [params] = useSearchParams();
  const focusLead = Number(params.get("lead")) || null;
  useEffect(() => {
    if (focusLead) setFilter("all");
  }, [focusLead]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [losing, setLosing] = useState(null); // lead being marked lost
  const [quoting, setQuoting] = useState(null); // { lead, customer }
  const [talking, setTalking] = useState(null); // lead id with its conversation open
  const linkedRef = useRef(false);

  const load = useCallback(async () => {
    setError("");
    try {
      setLeads(await listLeads());
    } catch (e) {
      setError(e.message || "Could not load the inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);

  const counts = useMemo(() => {
    const c = { new: 0, quoted: 0, won: 0, lost: 0, all: leads.length };
    for (const l of leads) c[l.stage] = (c[l.stage] || 0) + 1;
    return c;
  }, [leads]);

  const visible = useMemo(
    () => (filter === "all" ? leads : leads.filter((l) => l.stage === filter)),
    [leads, filter]
  );

  async function startQuote(lead) {
    setBusyId(lead.id);
    setError("");
    try {
      const customer = await customerForLead(lead.id);
      linkedRef.current = false;
      setQuoting({ lead, customer });
    } catch (e) {
      setError(e.message || "Could not prepare the customer for this lead.");
    } finally {
      setBusyId(null);
    }
  }

  async function markLost(lead, reason) {
    setBusyId(lead.id);
    try {
      await updateLead(lead.id, { status: "lost", lost_reason: reason });
      setLosing(null);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not update the lead.");
    } finally {
      setBusyId(null);
    }
  }

  async function reopen(lead) {
    setBusyId(lead.id);
    try {
      await updateLead(lead.id, { status: lead.quote_id ? "quoted" : "new", lost_reason: "" });
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not update the lead.");
    } finally {
      setBusyId(null);
    }
  }

  async function removeSpam(lead) {
    if (!window.confirm(`Delete this lead from ${lead.name || "unknown"} as spam?`)) return;
    setBusyId(lead.id);
    try {
      await deleteLead(lead.id);
      await load();
      notifyCountsChanged();
    } catch (e) {
      setError(e.message || "Could not delete the lead.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell
      title="Inbox"
      action={
        <Button onClick={() => setLogOpen(true)} className="py-2 shrink-0">
          + Log a lead
        </Button>
      }
    >
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
              filter === f.key
                ? "bg-birdie-green text-white"
                : "bg-white text-birdie-charcoal/65 border border-birdie-green/15"
            }`}
          >
            {f.label}
            {counts[f.key] > 0 && <span className="ml-1.5 opacity-70">{counts[f.key]}</span>}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner label="Loading inbox..." />
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-4 bg-white border border-birdie-green/15 rounded-2xl p-10 text-center">
          <div className="text-3xl mb-2">📭</div>
          <p className="text-sm text-birdie-charcoal/60">
            {filter === "new"
              ? "No new leads. Website enquiries land here automatically — log calls and texts with + Log a lead."
              : "Nothing here."}
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          {visible.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              focused={lead.id === focusLead}
              busy={busyId === lead.id}
              onQuote={() => startQuote(lead)}
              onLost={() => setLosing(lead)}
              onReopen={() => reopen(lead)}
              onSpam={() => removeSpam(lead)}
              onConversation={() => setTalking(lead.id)}
            />
          ))}
        </div>
      )}

      {logOpen && (
        <LogLeadModal
          onClose={() => setLogOpen(false)}
          onSaved={async () => {
            setLogOpen(false);
            setFilter("new");
            await load();
            notifyCountsChanged();
          }}
        />
      )}

      <Modal
        isOpen={Boolean(losing)}
        title="Why didn't it book?"
        onClose={() => setLosing(null)}
        maxWidthClass="max-w-md"
      >
        <div className="space-y-2">
          {LOST_REASONS.map((r) => (
            <button
              key={r}
              disabled={busyId !== null}
              onClick={() => markLost(losing, r)}
              className="w-full text-left px-4 py-3 rounded-xl border border-birdie-green/15 bg-white hover:bg-birdie-cream text-sm font-medium text-birdie-charcoal"
            >
              {r}
            </button>
          ))}
          <p className="text-xs text-birdie-charcoal/50 pt-1">
            The reason feeds the Numbers page, so you can see what's costing you jobs.
          </p>
        </div>
      </Modal>

      {talking && (
        <LeadConversation
          leadId={talking}
          onClose={(changed) => {
            setTalking(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}

      {quoting && (
        <QuoteBuilder
          quoteId={null}
          prefill={{
            customer: quoting.customer,
            title: quoting.lead.event_type || "",
            reference: referenceFor(quoting.lead),
          }}
          onSaved={async (saved) => {
            // Link once, on the first save; later saves are the same quote.
            if (linkedRef.current) return;
            linkedRef.current = true;
            try {
              await updateLead(quoting.lead.id, { quote_id: saved.id, status: "quoted" });
            } catch {
              linkedRef.current = false; // retry on the next save
            }
          }}
          onClose={() => {
            setQuoting(null);
            load().then(notifyCountsChanged);
          }}
        />
      )}
    </AppShell>
  );
}

function LeadCard({ lead, busy, onQuote, onLost, onReopen, onSpam, onConversation, focused }) {
  const [open, setOpen] = useState(focused);
  const cardRef = useRef(null);
  useEffect(() => {
    if (focused) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focused]);
  const badge = STAGE_BADGE[lead.stage] || STAGE_BADGE.new;
  const tel = telHref(lead.phone);
  const sms = smsHref(
    lead.phone,
    `Hi ${firstName(lead.name)}, it's ${senderName() || "the team"} from Birdie Bus — thanks for reaching out!`
  );
  const mail = mailtoHref(lead.email, "Your Birdie Bus enquiry");
  const attribution = [
    lead.gclid ? "Google Ads" : null,
    lead.utm_source && `${lead.utm_source}${lead.utm_medium ? ` / ${lead.utm_medium}` : ""}`,
    lead.utm_campaign,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      ref={cardRef}
      className={`bg-white border rounded-2xl p-3.5 ${
        focused ? "border-birdie-green ring-2 ring-birdie-green/30" : "border-birdie-green/15"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
              {badge.label}
            </span>
            <span className="text-[11px] text-birdie-charcoal/50">
              {SOURCE_LABEL[lead.source] || lead.source} · {timeAgo(lead.created_at)}
            </span>
          </div>
          {lead.customer_id ? (
            <RouterLink
              to={`/admin/customers/${lead.customer_id}`}
              className="block font-semibold text-birdie-charcoal truncate mt-1 hover:underline"
            >
              {lead.name || lead.email || lead.phone || "Unnamed lead"}
            </RouterLink>
          ) : (
            <div className="font-semibold text-birdie-charcoal truncate mt-1">
              {lead.name || lead.email || lead.phone || "Unnamed lead"}
            </div>
          )}
          {(lead.event_date_text || lead.event_type) && (
            <div className="text-xs text-birdie-charcoal/65 mt-0.5">
              {[lead.event_date_text && `📅 ${lead.event_date_text}`, lead.event_type]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>
        {lead.quote_number && (
          <div className="text-right shrink-0">
            <div className="font-mono text-[10px] text-birdie-charcoal/45">{lead.quote_number}</div>
            {lead.q_total_cents > 0 && (
              <div className="text-sm font-bold text-birdie-green">
                {formatUSD(lead.q_total_cents)}
              </div>
            )}
          </div>
        )}
      </div>

      {lead.message && (
        <button
          onClick={() => setOpen((v) => !v)}
          className={`mt-2 w-full text-left text-sm text-birdie-charcoal/75 ${
            open ? "whitespace-pre-line" : "line-clamp-2"
          }`}
        >
          {lead.message}
        </button>
      )}
      {lead.stage === "lost" && lead.lost_reason && (
        <div className="mt-1.5 text-xs text-birdie-charcoal/50">Lost: {lead.lost_reason}</div>
      )}
      {attribution && <div className="mt-1.5 text-[11px] text-birdie-charcoal/40">via {attribution}</div>}
      {campaignLine(lead) && (
        <div className="mt-1.5 text-[11px] text-birdie-charcoal/55">✉️ No-reply follow-ups: {campaignLine(lead)}</div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-birdie-green/10">
        {tel && <Link href={tel}>Call</Link>}
        {lead.phone && <Link href={sms}>Text</Link>}
        {mail && <Link href={mail}>Email</Link>}
        <Act onClick={onConversation}>Conversation</Act>
        <div className="flex-1" />
        {lead.stage === "new" && (
          <>
            <Act onClick={onLost} disabled={busy}>
              Lost
            </Act>
            <Act onClick={onQuote} disabled={busy} primary>
              {busy ? "…" : "Quote this"}
            </Act>
          </>
        )}
        {lead.stage === "lost" && !lead.quote_id && (
          <>
            <Act onClick={onSpam} disabled={busy} danger>
              Spam
            </Act>
            <Act onClick={onReopen} disabled={busy}>
              Reopen
            </Act>
          </>
        )}
      </div>
    </div>
  );
}

function Link({ href, children }) {
  return (
    <a
      href={href}
      className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-birdie-green/20 text-birdie-green hover:bg-birdie-cream"
    >
      {children}
    </a>
  );
}

function Act({ children, onClick, disabled, primary, danger }) {
  const style = primary
    ? "bg-birdie-green text-white hover:bg-birdie-green-dark"
    : danger
    ? "text-red-600 hover:bg-red-50"
    : "border border-birdie-green/20 text-birdie-charcoal/75 hover:bg-birdie-cream";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-40 whitespace-nowrap ${style}`}
    >
      {children}
    </button>
  );
}

function LogLeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    source: "phone",
    name: "",
    phone: "",
    email: "",
    event_date_text: "",
    event_type: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.name.trim() && !form.phone.trim() && !form.email.trim()) {
      setError("Add at least a name or a phone number.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createLead(form);
      await onSaved();
    } catch (e) {
      setError(e.message || "Could not save the lead.");
    } finally {
      setSaving(false);
    }
  }

  const input =
    "w-full px-3 py-2.5 text-sm border border-birdie-green/25 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-birdie-green/40";

  return (
    <Modal isOpen title="Log a lead" onClose={onClose} maxWidthClass="max-w-md">
      <div className="space-y-3">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          {["phone", "text", "other"].map((src) => (
            <button
              key={src}
              type="button"
              onClick={() => set("source", src)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${
                form.source === src
                  ? "bg-birdie-green text-white border-birdie-green"
                  : "bg-white text-birdie-charcoal/70 border-birdie-green/20"
              }`}
            >
              {SOURCE_LABEL[src]}
            </button>
          ))}
        </div>
        <input className={input} placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
        <input className={input} placeholder="Phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        <input className={input} placeholder="Email (optional)" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <input className={input} placeholder="Event date" value={form.event_date_text} onChange={(e) => set("event_date_text", e.target.value)} />
          <input className={input} placeholder="Event type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} />
        </div>
        <textarea className={input} rows={3} placeholder="What did they ask for?" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        <Button onClick={save} disabled={saving} className="w-full py-3">
          {saving ? <Spinner label="Saving..." /> : "Save lead"}
        </Button>
      </div>
    </Modal>
  );
}
